import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import SubscriptionCard from "@/components/SubscriptionCard";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [teamItems, setTeamItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("mine");

  useEffect(() => {
    Promise.all([
      api.get("/obituaries").catch(() => ({ data: { items: [] } })),
      user?.team_id ? api.get("/teams/obituaries").catch(() => ({ data: { items: [] } })) : Promise.resolve({ data: { items: [] } }),
    ]).then(([a, b]) => {
      setItems(a.data.items || []);
      setTeamItems((b.data.items || []).filter(it => it.author_id !== user?.id));
    }).finally(() => setLoading(false));
  }, [user?.team_id, user?.id]);

  const showing = tab === "mine" ? items : teamItems;

  return (
    <div style={{ maxWidth: 1100, margin: "40px auto", padding: "0 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 6 }}>YOUR LIBRARY</div>
          <h1 className="serif" style={{ fontSize: 40, color: "#fff", margin: 0, fontWeight: 400 }}>Welcome, {user?.name?.split(" ")[0] || "friend"}.</h1>
          <p className="muted" style={{ marginTop: 8 }}>Create a new obituary or revisit one you&apos;ve already composed.</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link to="/templates" data-testid="dash-templates" className="btn-ghost" style={{ textDecoration: "none" }}>Free Templates</Link>
          <Link to="/email-photos" data-testid="dash-email-photos" className="btn-ghost" style={{ textDecoration: "none" }}>Email Photos</Link>
          <Link to="/team" data-testid="dash-team" className="btn-ghost" style={{ textDecoration: "none" }}>{user?.team_id ? "Team Settings" : "Create Team"}</Link>
          <Link to="/new" data-testid="dash-new" className="btn-gold" style={{ textDecoration: "none" }}>+ New Obituary</Link>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <SubscriptionCard />
        <ChangePasswordCard />
      </div>

      {!user?.has_access && (
        <div className="bg-card-gold" style={{ padding: 18, marginBottom: 22, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div className="muted" style={{ fontSize: 14 }}>Your subscription is not active. Activate to use AI features.</div>
          <Link to="/paywall" className="btn-gold" style={{ textDecoration: "none", fontSize: 13, padding: "10px 18px" }}>Activate</Link>
        </div>
      )}

      {/* Tabs: My Library / Team Library */}
      {user?.team_id && (
        <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid #1a1a1a" }}>
          <button data-testid="tab-mine" onClick={() => setTab("mine")} style={{ background: "none", border: "none", color: tab === "mine" ? "var(--gold)" : "#888", padding: "12px 18px", cursor: "pointer", fontSize: 13, letterSpacing: "0.08em", borderBottom: tab === "mine" ? "2px solid var(--gold)" : "2px solid transparent" }}>MY LIBRARY ({items.length})</button>
          <button data-testid="tab-team" onClick={() => setTab("team")} style={{ background: "none", border: "none", color: tab === "team" ? "var(--gold)" : "#888", padding: "12px 18px", cursor: "pointer", fontSize: 13, letterSpacing: "0.08em", borderBottom: tab === "team" ? "2px solid var(--gold)" : "2px solid transparent" }}>TEAM LIBRARY ({teamItems.length})</button>
        </div>
      )}

      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="shimmer" style={{ height: 140, borderRadius: 14 }} />)}
        </div>
      ) : showing.length === 0 ? (
        <div className="bg-card" style={{ padding: 50, textAlign: "center" }}>
          <div style={{ fontSize: 40, color: "var(--gold)", marginBottom: 12 }}>✦</div>
          <div className="serif" style={{ fontSize: 24, color: "#fff", marginBottom: 8 }}>
            {tab === "mine" ? "Your library is empty" : "No teammate obituaries yet"}
          </div>
          <div className="muted" style={{ marginBottom: 18 }}>
            {tab === "mine" ? "Begin a new obituary — your draft will save here." : "When your teammates compose obituaries, they'll appear here."}
          </div>
          {tab === "mine" && <Link to="/new" data-testid="dash-empty-new" className="btn-gold" style={{ textDecoration: "none" }}>Begin Writing</Link>}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {showing.map(it => (
            <Link key={it.id} to={`/obituary/${it.id}`} data-testid={`obit-card-${it.id}`} className="bg-card" style={{ padding: 22, textDecoration: "none", color: "inherit", display: "block" }}>
              <div className="serif" style={{ fontSize: 22, color: "#fff", marginBottom: 4 }}>{it.deceased_name}</div>
              {tab === "team" && (
                <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>By {it.author_name}</div>
              )}
              <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>{(it.born || "?")} — {(it.died || "?")}</div>
              <div style={{ fontSize: 13, color: "#bbb", lineHeight: 1.55, height: 76, overflow: "hidden" }}>{it.body_preview}…</div>
              {tab === "team" && typeof it.progress === "number" && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ height: 4, background: "#1a1a1a", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${it.progress}%`, background: "var(--gold)" }} />
                  </div>
                  <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>{it.progress}% complete</div>
                </div>
              )}
              <div className="muted" style={{ fontSize: 11, marginTop: 12 }}>Updated {new Date(it.updated_at || it.created_at).toLocaleDateString()}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
